import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { buttonColor } from '@mwe/models';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'mwe-button',
  templateUrl: './button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, TranslatePipe],
})
export class ButtonComponent {
  private readonly router = inject(Router);

  readonly buttonId = input<string>();
  readonly labelKey = input<string>();
  readonly buttonType = input<HTMLButtonElement['type']>('button');
  readonly disabled = input<boolean>(false);
  readonly showSpinner = input<boolean>(false);
  readonly emitEventWithRoute = input<boolean>(false);
  readonly hasFullWidth = input<boolean>(false);
  readonly hasMinWidth = input<boolean>(false);
  readonly route = input<string>();
  readonly dummyRoute = input<string>();
  readonly url = input<string>();
  readonly color = input<buttonColor>('dark-blue');
  readonly category = input<'primary' | 'secondary' | 'tertiary'>('primary');
  readonly showHoverEffect = input<boolean>(false);
  readonly hasBlueBackground = input<boolean>(false);
  readonly size = input<'lg' | 'md' | 'sm' | 'xs'>('lg');
  readonly title = input<string>();
  readonly prependIconClass = input<string>();
  readonly appendIconClass = input<string>();
  readonly isIconBtn = input<boolean>(false);
  readonly active = input<boolean>(undefined);
  readonly ariaExpanded = input<true | false | null>(null);
  readonly ariaDescription = input<string>();

  readonly clicked = output<MouseEvent>();

  readonly waveCssClass = computed(() => (this.color() !== 'orange' ? `wave--${this.color()}` : ''));
  readonly btnCssClasses = computed<string[]>(() => {
    const btnTypeCss = `btn-${this.category()}--${this.color()}`;
    const hoverClass = this.showHoverEffect() && this.category() === 'primary' ? 'hover-effect' : '';
    const classes = ['btn', btnTypeCss, hoverClass];
    if (this.hasFullWidth()) {
      classes.push('w-100');
    }

    if (this.hasMinWidth()) {
      classes.push('minw-0');
    }

    if (this.isIconBtn()) {
      classes.push('btn-icon');
    }

    if (this.hasBlueBackground()) {
      classes.push('btn-focus-white');
    }

    if (this.size() && this.size() !== 'lg') {
      classes.push(`btn-${this.size()}`);
    }

    if (this.active()) {
      classes.push('active');
    }

    return classes;
  });

  async onClick($event: MouseEvent): Promise<void> {
    if (this.disabled()) {
      $event.preventDefault();
      return;
    }

    if (this.route()) {
      if (this.dummyRoute()) {
        // can be used for rerendering a component with complex initialization logic on same URL
        // and for correct browser site-stack logic when navigating backwards
        await this.router.navigateByUrl(this.dummyRoute(), { replaceUrl: true });
      }

      this.router.navigateByUrl(this.route());

      if (this.emitEventWithRoute()) {
        this.clicked.emit($event);
      }
      return;
    }

    if (this.url()) {
      window.open(this.url(), '_blank');
      return;
    }

    this.clicked.emit($event);
  }
}
